import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, required, textarea }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] text-ink/50">{label}</span>
    {textarea ? (
      <textarea
        data-testid={`contact-${name}`}
        name={name}
        rows={3}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 w-full bg-transparent border-b border-maroon/25 py-2 text-ink focus:border-maroon outline-none transition-colors resize-none"
      />
    ) : (
      <input
        data-testid={`contact-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 w-full bg-transparent border-b border-maroon/25 py-2 text-ink focus:border-maroon outline-none transition-colors"
      />
    )}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Overline>Get in Touch</Overline>
          <h2 className="ff-display text-4xl md:text-5xl tracking-tight text-ink leading-[1.02]">
            We would love to hear from your family.
          </h2>
          <p className="mt-5 text-ink/70 font-light max-w-md">
            Questions about admissions, curriculum or a campus visit? Send us a note
            and our team will respond within one working day.
          </p>

          <div className="mt-10 space-y-6">
            {[
              { icon: MapPin, t: "Campus", d: "Kotpad, Koraput, Odisha" },
              { icon: Phone, t: "Phone", d: "+91 70086 46951" },
              { icon: Mail, t: "Email", d: "bsvnschool7@gmail.com" },
            ].map((c) => (
              <div key={c.t} className="flex items-start gap-4">
                <span className="grid place-items-center h-10 w-10 rounded-full border border-maroon/25 text-maroon shrink-0">
                  <c.icon size={17} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-ink/50">{c.t}</div>
                  <div className="text-ink">{c.d}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <form data-testid="contact-form" onSubmit={submit} className="border border-maroon/15 p-8 md:p-10 space-y-6 bg-cream">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Full Name" name="name" value={form.name} onChange={onChange} required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
              <Field label="Subject" name="subject" value={form.subject} onChange={onChange} />
            </div>
            <Field label="Message" name="message" value={form.message} onChange={onChange} required textarea />
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="w-full py-3.5 rounded-full bg-maroon text-cream tracking-wide hover:bg-saffron hover:text-ink transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
